import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../hooks/useAuthContext';
import { supabase } from '../../config/supabase';

export default function AdminProtectedRoute({ children }) {
  const { session, isLoading } = useAuthContext();
  const navigate = useNavigate();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (isLoading) return;

      if (!session) {
        navigate('/admin/login');
        return;
      }

      try {
        // Fetch user profile to check role
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, is_admin, is_superadmin')
          .eq('id', session.user.id)
          .single();

        console.log('AdminProtectedRoute Debug:', {
          profile,
          profileError,
          is_admin: profile?.is_admin,
          is_superadmin: profile?.is_superadmin
        });

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          navigate('/admin/login');
          return;
        }

        // Check if user has admin role
        if (profile.is_admin !== true) {
          console.log('❌ User does not have admin privileges');
          // If user is superadmin, redirect to superadmin dashboard
          if (profile.is_superadmin === true) {
            console.log('⚠️ User is SuperAdmin, redirecting to /superadmin');
            navigate('/superadmin');
          } else {
            console.log('❌ User has no admin privileges, redirecting to login');
            navigate('/admin/login');
          }
          return;
        }

        // Check if user has a related brand and if it's active
        let brandId = null;

        // First, try to get brand from brand_managers table
        const { data: brandManager, error: brandManagerError } = await supabase
          .from('brand_managers')
          .select('brand_id')
          .eq('profile_id', session.user.id)
          .maybeSingle();

        if (brandManager && brandManager.brand_id) {
          brandId = brandManager.brand_id;
          console.log('Found brand_id from brand_managers:', brandId);
        } else {
          // Fallback: Try to find brand where user is the creator
          const { data: userBrand, error: brandError } = await supabase
            .from('brands')
            .select('id')
            .eq('created_by', session.user.id)
            .maybeSingle();

          if (userBrand && userBrand.id) {
            brandId = userBrand.id;
            console.log('Found brand_id from brands table (created_by):', brandId);
          }
        }

        // Check if user has a related brand
        if (!brandId) {
          console.log('❌ User does not have a related brand');
          navigate('/admin/login');
          return;
        }

        // Fetch brand details to check if it's active
        const { data: brand, error: brandDataError } = await supabase
          .from('brands')
          .select('id, name, active')
          .eq('id', brandId)
          .single();

        if (brandDataError || !brand) {
          console.error('Error fetching brand data:', brandDataError);
          navigate('/admin/login');
          return;
        }

        // Check if brand is active
        if (brand.active !== true) {
          console.log('❌ User\'s brand is not active:', brand);
          navigate('/admin/login');
          return;
        }

        // All checks passed - grant access
        console.log('✅ User is admin with active brand, granting access');
        setHasAccess(true);
      } catch (err) {
        console.error('Error checking access:', err);
        navigate('/admin/login');
      } finally {
        setCheckingAccess(false);
      }
    };

    checkAccess();
  }, [session, isLoading, navigate]);

  if (isLoading || checkingAccess) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh'
      }}>
        Loading...
      </div>
    );
  }

  if (!session || !hasAccess) {
    return null;
  }

  return <>{children}</>;
}

